import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail } from "lucide-react";
import { CONTACT_EMAIL } from "../../data/site";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPICS = ["Booking", "Feature", "Press", "Other"];
const field = "w-full border-b border-[var(--line)] bg-transparent py-4 text-lg text-[var(--bone)] placeholder:text-zinc-500 focus:border-[var(--cash)] focus:outline-none transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", topic: "Booking", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", topic: "Booking", message: "" });
    } catch {
      toast.error(`Couldn't send. Email us at ${CONTACT_EMAIL}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
      <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow num="05">Contact</Eyebrow>
          <h2 className="mt-6 font-display text-5xl uppercase leading-[0.9] sm:text-7xl">Let's get <span className="font-serif-i normal-case text-[var(--cash)]">paid.</span></h2>
          <p className="mt-6 max-w-sm text-zinc-300">Bookings, features, press or anything else — drop a message or email us directly.</p>
          <a data-testid="contact-email-link" href={`mailto:${CONTACT_EMAIL}`}
            className="group mt-10 flex w-fit items-center gap-4 rounded-full border border-[var(--line)] py-2 pl-2 pr-6 transition-colors hover:border-[var(--cash)]">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--cash)] text-[var(--ink)]"><Mail size={18} /></span>
            <span className="break-all text-base font-semibold sm:text-lg">{CONTACT_EMAIL}</span>
            <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
          </a>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="rounded-[28px] border border-[var(--line)] bg-[var(--ink-2)] p-6 sm:p-10">
            <div className="flex flex-wrap gap-2">
              {TOPICS.map((t) => (
                <button type="button" key={t} data-testid={`contact-topic-${t.toLowerCase()}`} onClick={() => setForm({ ...form, topic: t })}
                  className={`rounded-full border px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${form.topic === t ? "border-[var(--cash)] bg-[var(--cash)] text-[var(--ink)]" : "border-[var(--line)] text-zinc-300 hover:border-zinc-400"}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="mt-6 grid gap-2 sm:grid-cols-2 sm:gap-8">
              <input required data-testid="contact-name-input" className={field} placeholder="Your name" value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="contact-email-input" className={field} placeholder="Your email" value={form.email} onChange={set("email")} />
            </div>
            <textarea required rows={4} data-testid="contact-message-input" className={`${field} mt-2 resize-none`} placeholder="Tell us what's up" value={form.message} onChange={set("message")} />
            <button type="submit" disabled={sending} data-testid="contact-submit-btn"
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[var(--cash)] py-5 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[var(--ink)] transition-transform hover:scale-[1.02] disabled:opacity-60">
              {sending ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />} Send message
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
